import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ExternalLink, Flag, FlagOff, Send, X, Link2,
  FileText, Copy, Check, Mail, MessageSquare, GitCommitHorizontal, ArrowRight,
  FilePlus, CheckCircle2, Banknote, Inbox,
} from 'lucide-react';

// Subtle event categorisation for the activity timeline (colours the icon only).
const CAT = {
  note: { cls: 'bg-primary/15 text-primary', icon: MessageSquare },
  email: { cls: 'bg-sky-500/12 text-sky-600', icon: Mail },
  created: { cls: 'bg-emerald-500/12 text-emerald-600', icon: FilePlus },
  posted: { cls: 'bg-primary/12 text-primary', icon: CheckCircle2 },
  payment: { cls: 'bg-amber-500/12 text-amber-600', icon: Banknote },
  received: { cls: 'bg-sky-500/12 text-sky-600', icon: Inbox },
  change: { cls: 'bg-muted text-muted-foreground', icon: GitCommitHorizontal },
};

function classify(it) {
  if (it.source === 'sangly') return 'note';
  if (it.type === 'email') return 'email';
  const text = `${it.body || ''} ${(it.changes || []).map((c) => `${c.field} ${c.to}`).join(' ')}`.toLowerCase();
  if (/\bcreated\b|créé|aangemaakt/.test(text)) return 'created';
  if (/comptabilis|\bposted\b|validat|geboekt/.test(text)) return 'posted';
  if (/payment|\bbank\b|iban|betaald|\bpaid\b/.test(text)) return 'payment';
  if (/peppol|received|reçu|verzonden|sent successfully/.test(text)) return 'received';
  return 'change';
}
import { api } from '../api/sangly';
import PetanqueLoader from './PetanqueLoader';
import MentionTextarea from './MentionTextarea';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { money, moneyExact, fmtDate, duePhrase, timeAgo, BLOCKED, PAYMENT, PRIORITY } from '../lib/format';
import { cn } from '../lib/utils';

function StatusBadge({ inv }) {
  const p = PAYMENT[inv.payment_state] || PAYMENT.not_paid;
  const pr = PRIORITY[inv.priority_label] || PRIORITY.low;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant={p.variant}>{p.label}</Badge>
      {!['paid', 'draft'].includes(inv.payment_state) && <Badge variant={pr.variant}>{pr.label} priority</Badge>}
      {inv.is_flagged && <Badge variant="accent"><Flag className="h-3 w-3" /> Following</Badge>}
    </div>
  );
}

export default function InvoiceDetailDialog({ invoiceId, open, onClose, onChanged, users = [] }) {
  const [inv, setInv] = useState(null);
  const [comments, setComments] = useState([]);
  const [chatter, setChatter] = useState([]);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);      // { url, type } | null
  const [previewLoading, setPreviewLoading] = useState(false);

  const clearPreview = () => setPreview((p) => { if (p) URL.revokeObjectURL(p.url); return null; });

  const togglePreview = async () => {
    if (preview) { clearPreview(); return; }
    setPreviewLoading(true);
    try {
      const blob = await api.invoices.pdfBlob(invoiceId);
      setPreview({ url: URL.createObjectURL(blob), type: blob.type || '' });
    } catch { /* ignore - button stays available */ }
    finally { setPreviewLoading(false); }
  };
  // revoke the object URL when the invoice changes / dialog unmounts
  useEffect(() => () => clearPreview(), [invoiceId]);

  const copyPaymentRef = () => {
    if (!inv?.payment_reference) return;
    navigator.clipboard?.writeText(inv.payment_reference).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.invoices.get(invoiceId), api.invoices.comments(invoiceId)]);
      setInv(d);
      setComments(c);
    } finally { setLoading(false); }
    // Odoo chatter is a separate (slower) call - load it without blocking the dialog.
    api.invoices.chatter(invoiceId).then((r) => setChatter(r.items || [])).catch(() => setChatter([]));
  }, [invoiceId]);

  // Payment facts (paid date / planned date + proof) - only for paid / in-payment bills.
  useEffect(() => {
    setPayments(null);
    if (!invoiceId || !inv || !['paid', 'in_payment', 'partial'].includes(inv.payment_state)) return;
    api.invoices.payments(invoiceId).then(setPayments).catch(() => setPayments(null));
  }, [invoiceId, inv?.payment_state]);   // eslint-disable-line react-hooks/exhaustive-deps

  const openProof = async (attId) => {
    try {
      const blob = await api.invoices.proofBlob(invoiceId, attId);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { /* ignore */ }
  };

  const [proofLoading, setProofLoading] = useState(false);
  const openProofDoc = async () => {
    setProofLoading(true);
    try {
      const blob = await api.invoices.proofPdf(invoiceId);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { /* ignore */ } finally { setProofLoading(false); }
  };

  useEffect(() => { if (open) { setBody(''); setMentions([]); load(); } }, [open, load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const updated = await api.invoices.update(invoiceId, patch);
      setInv(updated);
      onChanged?.();
    } finally { setSaving(false); }
  };

  const toggleFlag = async () => {
    setSaving(true);
    try {
      const updated = inv.is_flagged ? await api.invoices.unflag(invoiceId) : await api.invoices.flag(invoiceId);
      setInv(updated);
      onChanged?.();
    } finally { setSaving(false); }
  };

  const postComment = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      const c = await api.invoices.addComment(invoiceId, body.trim(), mentions);
      setComments((prev) => [...prev, c]);
      setBody(''); setMentions([]); setShowTag(false);
      onChanged?.();
    } finally { setPosting(false); }
  };

  // Unified activity feed: internal Sangly notes + Odoo chatter, newest first.
  const timeline = useMemo(() => {
    const mine = comments.map((c) => ({
      key: `c${c.id}`, source: 'sangly', date: c.created_at,
      author: c.author_name || 'Someone', body: c.body, mentions: c.mentions || [],
    }));
    const odoo = chatter.map((m) => ({
      key: `o${m.id}`, source: 'odoo', date: m.date, author: m.author || 'Odoo',
      body: m.body, type: m.type, changes: m.changes || [],
    }));
    return [...mine, ...odoo].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [comments, chatter]);

  return (
    <Dialog open={open} onClose={onClose} className="max-w-4xl p-0">
      {loading || !inv ? (
        <div className="grid h-64 place-items-center"><PetanqueLoader size={38} roll /></div>
      ) : (
        <div className="max-h-[85vh] overflow-y-auto">
          {/* header */}
          <div className="border-b border-border p-5">
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="min-w-0">
                <div className="truncate text-lg font-semibold">{inv.supplier || '-'}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-muted-foreground">
                  <span className="font-mono">{inv.name || '-'}</span>
                  {inv.ref && <span>Bill ref <span className="font-mono text-foreground/80">{inv.ref}</span></span>}
                </div>
              </div>
            </div>
            <div className="mt-3"><StatusBadge inv={inv} /></div>
          </div>

          {/* amounts */}
          <div className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-4">
            {[
              ['Amount due', money(inv.amount_residual, inv.currency)],
              ['Total', money(inv.amount_total, inv.currency)],
              ['Invoice date', fmtDate(inv.invoice_date)],
              ['Due date', <span key="d" className={cn(inv.is_overdue && 'text-rose-600')}>
                {fmtDate(inv.date_due)}<span className="block text-[11px]">{duePhrase(inv.days_overdue)}</span>
              </span>],
            ].map(([k, v]) => (
              <div key={k} className="bg-card px-4 py-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div>
                <div className="mt-0.5 text-[14px] font-semibold tnum">{v}</div>
              </div>
            ))}
          </div>

          {/* the HR portal's side of an expense note: who filed it, when, who approved it */}
          {inv.hr && (
            <div className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-4">
              {[
                ['Employee', <span key="e">{inv.hr.employee}
                  {inv.hr.department && <span className="block text-[11px] font-normal text-muted-foreground">{inv.hr.department}</span>}
                  {inv.hr.url && <a href={inv.hr.url} target="_blank" rel="noreferrer" className="block text-[11px] font-normal text-primary hover:underline">Open the note in the HR portal</a>}
                </span>],
                ['Filed', fmtDate(inv.hr.submitted_at)],
                ['Approved', <span key="a">{fmtDate(inv.hr.approved_at)}
                  {inv.hr.approved_by && <span className="block text-[11px] font-normal text-muted-foreground">by {inv.hr.approved_by}</span>}
                </span>],
                // the note's own total · Odoo's draft can be one receipt short
                ['Note amount', <span key="m" className="tnum">{money(inv.hr.amount_tvac, inv.currency)}
                  {Math.abs(Number(inv.hr.amount_tvac) - Number(inv.amount_total)) > 0.005 && (
                    <span className="block text-[11px] font-normal text-rose-600">Odoo bill: {money(inv.amount_total, inv.currency)}</span>
                  )}
                </span>],
              ].map(([k, v]) => (
                <div key={k} className="bg-card px-4 py-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div>
                  <div className="mt-0.5 text-[14px] font-semibold">{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* payment reference (what to put on the bank transfer) */}
          {inv.payment_reference && (
            <div className="flex items-center gap-3 border-b border-border px-5 py-3">
              <span className="text-[12px] text-muted-foreground">Payment reference</span>
              <span className="font-mono text-[13px] font-medium">{inv.payment_reference}</span>
              <button onClick={copyPaymentRef}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground">
                {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
              </button>
            </div>
          )}

          {/* payment facts - paid / planned date + amount + proof of payment */}
          {payments?.payments?.length > 0 && (
            <div className="flex flex-col gap-1.5 border-b border-border bg-emerald-500/[0.06] px-5 py-3">
              {payments.payments.map((p, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                  <Banknote className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">
                    {inv.payment_state === 'paid' ? 'Paid' : inv.payment_state === 'partial' ? 'Partly paid' : 'Payment planned'}
                    {p.date ? ` ${fmtDate(p.date)}` : ''}
                  </span>
                  {p.amount != null && <span className="tnum text-muted-foreground">· {money(p.amount, inv.currency)}</span>}
                  {p.journal && <span className="text-muted-foreground">· {p.journal}</span>}
                  {p.ref && <span className="text-muted-foreground">· {p.ref}</span>}
                  {(p.proofs || []).map((pr) => (
                    <button key={pr.id} onClick={() => openProof(pr.id)}
                      className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-primary hover:bg-muted">
                      <FileText className="h-3 w-3" /> Proof
                    </button>
                  ))}
                  {p.odoo_url && (
                    <a href={p.odoo_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
                      Payment <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
              {inv.payment_state === 'paid' && (
                <div className="pt-1">
                  <button onClick={openProofDoc} disabled={proofLoading}
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600/40 bg-emerald-600/10 px-2.5 py-1 text-[12px] font-medium text-emerald-700 hover:bg-emerald-600/15 disabled:opacity-60">
                    {proofLoading ? <PetanqueLoader size={14} /> : <FileText className="h-3.5 w-3.5" />} Proof of payment (PDF)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* editable controls */}
          <div className="grid gap-4 border-b border-border p-5 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-[12px] font-medium">Account status</label>
              <div className="flex h-10 items-center gap-2">
                <Badge variant={(BLOCKED[inv.account_status] || BLOCKED[1]).variant}>
                  {(BLOCKED[inv.account_status] || BLOCKED[1]).label}
                </Badge>
                <span className="text-[11px] text-muted-foreground">set at vendor level</span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-[12px] font-medium">Gorgias ticket URL</label>
              <div className="flex gap-2">
                <Input type="url" placeholder="https://lamptwist.gorgias.com/tickets/…"
                       defaultValue={inv.gorgias_url}
                       onBlur={(e) => { if (e.target.value !== inv.gorgias_url) save({ gorgias_url: e.target.value }); }} />
                {inv.gorgias_url && (
                  <a href={inv.gorgias_url} target="_blank" rel="noreferrer"
                     className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-border hover:bg-muted" title="Open Gorgias ticket">
                    <Link2 className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* actions */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-5">
            <Button variant={inv.is_flagged ? 'secondary' : 'default'} onClick={toggleFlag} disabled={saving}>
              {inv.is_flagged ? <><FlagOff className="h-4 w-4" /> Stop following</> : <><Flag className="h-4 w-4" /> Follow up</>}
            </Button>
            {(inv.pdf_url || inv.attachment_name) && (
              <Button variant={preview ? 'secondary' : 'outline'} onClick={togglePreview} disabled={previewLoading}>
                {previewLoading ? <PetanqueLoader size={16} /> : <FileText className="h-4 w-4" />}
                {preview ? 'Hide bill' : 'View bill'}
              </Button>
            )}
            {inv.odoo_url && (
              <a href={inv.odoo_url} target="_blank" rel="noreferrer">
                <Button variant="outline"><ExternalLink className="h-4 w-4" /> Open in Odoo</Button>
              </a>
            )}
            {inv.is_flagged && inv.flagged_by_name && (
              <span className="text-[12px] text-muted-foreground">
                Followed by <span className="font-medium text-foreground">{inv.flagged_by_name}</span>
                {inv.flagged_at && ` · ${timeAgo(inv.flagged_at)}`}
              </span>
            )}
            {saving && <PetanqueLoader size={18} />}
          </div>

          {/* inline bill preview */}
          {preview && (
            <div className="border-b border-border bg-muted/30 p-3">
              {preview.type.startsWith('image/') ? (
                <img src={preview.url} alt="Bill" className="mx-auto max-h-[72vh] rounded-lg border border-border bg-white" />
              ) : (
                <iframe src={preview.url} title="Bill" className="h-[72vh] w-full rounded-lg border border-border bg-white" />
              )}
            </div>
          )}

          {/* activity timeline */}
          <div className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] font-semibold">Activity</span>
              <span className="text-[11px] text-muted-foreground">Odoo chatter + internal notes</span>
            </div>

            {/* composer */}
            <div className="mb-4 rounded-lg border border-border p-3">
              <MentionTextarea rows={3} placeholder="Add an update - type @ to mention a colleague…"
                               value={body} onChange={setBody} users={users}
                               onMention={(u) => setMentions((prev) => prev.some((m) => m.id === u.id) ? prev : [...prev, u])} />
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {mentions.map((m) => (
                  <Badge key={m.id} variant="accent" className="cursor-pointer"
                         onClick={() => setMentions((prev) => prev.filter((x) => x.id !== m.id))}>
                    @{m.name} <X className="h-3 w-3" />
                  </Badge>
                ))}
                <Button size="sm" className="ml-auto" onClick={postComment} disabled={posting || !body.trim()}>
                  {posting ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Post
                </Button>
              </div>
            </div>

            {/* timeline */}
            {timeline.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">No activity yet.</p>
            ) : (
              <ol className="relative space-y-3.5 border-l border-border pl-5">
                {timeline.map((it) => {
                  const meta = CAT[classify(it)] || CAT.change;
                  const Icon = meta.icon;
                  return (
                    <li key={it.key} className="relative">
                      <span className={cn('absolute -left-[27px] grid h-6 w-6 place-items-center rounded-full ring-4 ring-card', meta.cls)}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-[13px] font-medium">{it.author}</span>
                        <Badge variant={it.source === 'sangly' ? 'accent' : 'outline'}>
                          {it.source === 'sangly' ? 'Note' : 'Odoo'}
                        </Badge>
                        <span className="ml-auto text-[11px] text-muted-foreground">{timeAgo(it.date)}</span>
                      </div>
                      {it.body && <div className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed">{it.body}</div>}
                      {it.changes?.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {it.changes.map((c, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
                              <span className="font-medium text-foreground/80">{c.field}</span>
                              <span>{c.from || '-'}</span>
                              <ArrowRight className="h-3 w-3" />
                              <span className="text-foreground">{c.to || '-'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {it.mentions?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {it.mentions.map((m) => <Badge key={m.id} variant="accent">@{m.name}</Badge>)}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
